import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { toast } from '../components/Toast';

const PASSWORD_STRENGTH_LABELS = ['弱', '弱', '一般', '强', '很强'];
const PASSWORD_STRENGTH_COLORS = ['bg-danger', 'bg-danger', 'bg-warning', 'bg-success', 'bg-success'];

function getStrength(pwd: string): number {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  return Math.min(score, 4);
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = getStrength(password);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.register({ email, password, nickname });
      // 注册成功后自动登录，跳过邮箱验证步骤（MVP 阶段）
      try {
        await authApi.login({ email, password });
      } catch {
        // 自动登录失败时降级到登录页，并提示已注册
        navigate('/login?registered=1');
        return;
      }
      toast.success('注册成功，已自动登录');
      navigate('/spaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : '注册失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* 左侧品牌 */}
      <div className="hidden flex-1 flex-col justify-between bg-bg-surface p-12 md:flex">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-sm font-bold text-text-inverse shadow-sm">
            C
          </div>
          <span className="text-xl font-semibold tracking-tight">CoMind</span>
        </Link>
        <div className="space-y-6">
          <div className="h-32 w-48 rounded-2xl bg-accent/5 ring-1 ring-accent/10" />
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-[#1a1a2e]">小团队，大协作</h2>
            <p className="mt-2 text-text-secondary">文档与思维导图，一个工具搞定</p>
          </div>
        </div>
        <div className="text-sm text-text-tertiary">© 2026 CoMind</div>
      </div>

      {/* 右侧表单 */}
      <div className="flex flex-1 items-center justify-center bg-bg-canvas p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">创建账号</h1>
            <p className="mt-1 text-sm text-text-secondary">免费注册，立即开始协作</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="nickname">
                昵称
              </label>
              <input
                id="nickname"
                type="text"
                required
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="input"
                placeholder="你的昵称"
                maxLength={32}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="email">
                邮箱
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="password">
                密码
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="≥8 位，含字母和数字"
                minLength={8}
              />
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                    <div
                      className={`h-full rounded-full transition-all ${PASSWORD_STRENGTH_COLORS[strength]}`}
                      style={{ width: `${(strength + 1) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-tertiary">{PASSWORD_STRENGTH_LABELS[strength]}</span>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-danger/5 border border-danger/20 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn btn-primary btn-md w-full shadow-sm">
              {loading ? '注册中...' : '注册'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-text-secondary">
            已有账号？{' '}
            <Link to="/login" className="text-accent hover:underline font-medium">
              立即登录
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}